import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box, Stack } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/system/Unstable_Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Alert, Autocomplete, Button, MenuItem, TextField, Typography } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';
import { useCustomFields } from 'src/hooks/use-custom-fields';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { ICompanyProfile } from 'src/services/connections/connections.types';
import { useAddContactMutation } from 'src/services/contacts/contacts.service';
import { ContactType, ContactSource, Contact } from 'src/services/contacts/contacts.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetCompanyByIdQuery, useGetMyCompaniesQuery } from 'src/services/connections/connections.service';

import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';

import { clearLatestCreatedCompany, showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

import { TenantType } from 'src/types/enums';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onSuccess?: (data: Contact) => void;
  companyId?: string;
};

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

export default function AddContactModal({
  open,
  onClose,
  onSuccess = () => { },
  companyId = undefined
}: Props) {
  const { t } = useTranslate();

  const authContext = useAuthContext();

  const tenantType = useOrgTenant();

  const dispatch = useAppDispatch();
  const latestCreatedCompany = useAppSelector((state) => state.company.latestCreatedCompany);

  const companyLinkControl = useBoolean(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [companySearchQuery, setCompanySearchQuery] = useState<string>("");

  const [addContact] = useAddContactMutation();

  // Custom fields hook
  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.CONTACT
  });

  const { data: scopedCompanyProfile } = useGetCompanyByIdQuery({ profile_id: companyId as string }, {
    skip: !companyId,
  });

  const { currentData: companies } = useGetMyCompaniesQuery(
    {
      params: {
        ...DEFAULT_PAGE_OPTIONS,
        search_query: companySearchQuery
      },
    }
  );

  const AddContactSchema = Yup.object().shape({
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string().email(t('validation.email_format')).required(t('validation.required')),
    phone: Yup.string().nullable(),
    company_profile: Yup.mixed<ICompanyProfile>().nullable(),
    type: Yup.string().required(t('validation.required'))
  });

  const methods = useForm({
    resolver: yupResolver(AddContactSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_profile: null,
      type: ''
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting },
  } = methods;

  const selectedCompanyProfile = watch('company_profile') as ICompanyProfile | null;

  const handleClose = () => {
    setErrorMessage(null);
    companyLinkControl.onFalse();
    setCompanySearchQuery('');
    dispatch(clearLatestCreatedCompany());
    reset();
    onClose();
  }

  const handleOpenCompanyLinkDialog = () => {
    companyLinkControl.onTrue();
  }

  const handleCloseCompanyLinkDialog = () => {
    companyLinkControl.onFalse();
    setCompanySearchQuery('');
  }

  const handleAddNewCompany = () => {
    dispatch(showCompanyEditor(undefined));
  }

  useEffect(() => {
    if (scopedCompanyProfile && open) {
      setValue('company_profile', scopedCompanyProfile, { shouldValidate: true });
    }
  }, [scopedCompanyProfile, setValue, open]);

  useEffect(() => {
    if (latestCreatedCompany && open && !scopedCompanyProfile) {
      setValue('company_profile', latestCreatedCompany as ICompanyProfile, { shouldValidate: true, shouldDirty: true });
      setCompanySearchQuery(latestCreatedCompany.company_name);
      companyLinkControl.onFalse();
      dispatch(clearLatestCreatedCompany());
    }
  }, [latestCreatedCompany, open, scopedCompanyProfile, setValue, dispatch, companyLinkControl]);

  const handleAddContact = handleSubmit(async (data) => {
    setErrorMessage(null);

    // Validate custom fields first
    const customFieldValidation = customFields.validate();
    if (!customFieldValidation.isValid) {
      return;
    }

    try {
      // create contact first
      const contactResponse = await addContact({
        org_id: authContext.organization?.id as string,
        manual_contact: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone as string,
          company_name: data.company_profile ? (data.company_profile as ICompanyProfile).company_name as string : undefined,
          company_profile_id: data.company_profile ? (data.company_profile as ICompanyProfile).id : undefined,
          type: data.type as ContactType,
          custom_fields: customFields.getData()
        },
        source: ContactSource.MANUAL_LINK,
      }).unwrap();

      enqueueSnackbar(t('contacts.api.add.success'), { variant: 'success' });
      reset();
      handleClose();
      onSuccess(contactResponse);
    } catch (e) {
      console.error(e);

      setErrorMessage(t('contacts.api.add.default_error'));
      enqueueSnackbar(t('contacts.api.add.default_error'), { variant: 'error' });
    }
  });

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle sx={{ textTransform: 'capitalize' }}>
        {t('contacts.add_modal.title')}
      </DialogTitle>

      <DialogContent>
        {errorMessage && (
          <Alert variant="outlined" severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}
        <FormProvider methods={methods} onSubmit={handleAddContact}>
          <Grid container spacing={2} justifyContent="center" pt={1}>
            <Grid xs={12}>
              <Alert variant="outlined" severity="info">
                {t(`contacts.add_modal.alert`)}
              </Alert>
            </Grid>

            <Grid xs={6}>
              <RHFTextField
                name="first_name"
                label={t('common.form_labels.first_name')}
                required
                fullWidth
              />
            </Grid>

            <Grid xs={6}>
              <RHFTextField
                name="last_name"
                label={t('common.form_labels.last_name')}
                required
                fullWidth
              />
            </Grid>

            <Grid xs={12}>
              <RHFTextField name="email" label={t('common.form_labels.email')} required fullWidth />
            </Grid>

            <Grid xs={12}>
              <RHFTextField name="phone" label={t('common.form_labels.phone')} fullWidth />
            </Grid>

            <Grid xs={12}>
              <Stack direction="column" spacing={1}>
                {tenantType !== TenantType.Candidate && (
                  <>
                    <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                      <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                        <Iconify icon="material-symbols:dataset-linked-outline" />
                        <Box>{t('contacts.add_modal.company_label')}</Box>
                      </Stack>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenCompanyLinkDialog}
                        disabled={!!scopedCompanyProfile}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                        sx={{
                          color: 'text.primary',
                          textTransform: 'capitalize',
                        }}
                      >
                        {t('contacts.add_modal.add_new')}
                      </Button>
                    </Stack>

                    <RHFAutocomplete
                      multiple={false}
                      fullWidth
                      label={t('contacts.detail.linked_companies.search')}
                      onInputChange={(_e, value) => onSearchQueryChange(value)}
                      name="company_profile"
                      disabled={!!scopedCompanyProfile}
                      placeholder={t('companies.profile.linked_companies.search_placeholder')}
                      options={companies?.results as ICompanyProfile[] ?? []}
                      filterOptions={(x) => x}
                      renderOption={({ key, ...rest }, option: ICompanyProfile) => (
                        <li key={key} {...rest}>
                          <Stack direction="column">
                            <Typography variant="body1">
                              {`${option.company_name}`}
                            </Typography>
                          </Stack>
                        </li>
                      )}
                      getOptionLabel={(option) => (option as ICompanyProfile).company_name as string}
                      isOptionEqualToValue={(option, value) =>
                        option && (option as ICompanyProfile).id === (value as ICompanyProfile).id
                      }
                      getOptionKey={(option) => (option as ICompanyProfile).id as string}
                    />
                  </>
                )}
              </Stack>

            </Grid>

            <Grid xs={12}>
              <RHFSelect name="type" label={t('contacts.add_modal.contact_type')} inputMode='text' required>
                <MenuItem value="" disabled>
                  {t('common.none')}
                </MenuItem>
                {Object.values(ContactType).map((value: ContactType) => (
                  <MenuItem key={value} value={value}>
                    {t(`enums.contact_type.${value}`)}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>

            {/* Custom Fields */}
            {customFields.hasCustomFields && (
              <>
                {customFields.renderCustomFields}
              </>
            )}

            <Stack sx={{ py: 2, width: '100%' }} flexDirection="row" justifyContent="flex-end">
              <LoadingButton
                loading={isSubmitting}
                type="submit"
                variant="contained"
                disabled={!isValid}
              >
                {t('contacts.add')}
              </LoadingButton>
            </Stack>
          </Grid>
        </FormProvider>
      </DialogContent>
    </Dialog>

      <ConfirmDialog
        open={companyLinkControl.value}
        title={
          <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center">
              <Iconify icon="material-symbols:dataset-linked-outline" />
              <Box>{t('contacts.detail.linked_companies.dialog_title')}</Box>
            </Stack>
            <Button variant="text" onClick={handleAddNewCompany} startIcon={<Iconify icon="eva:plus-fill" />}>
              {t('common.new')}
            </Button>
          </Stack>
        }
        onClose={handleCloseCompanyLinkDialog}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            options={companies?.results as ICompanyProfile[] ?? []}
            value={selectedCompanyProfile ?? null}
            onInputChange={(_e, value) => onSearchQueryChange(value)}
            onChange={(_e, value: ICompanyProfile | null) => {
              setValue('company_profile', value, { shouldValidate: true, shouldDirty: true });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('contacts.detail.linked_companies.search')}
                placeholder={t('contacts.detail.linked_companies.search_placeholder')}
              />
            )}
            renderOption={({ key, ...rest }, option: ICompanyProfile) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.company_name}`}
                  </Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => option.company_name as string}
            isOptionEqualToValue={(option, value) =>
              option && (option as ICompanyProfile).id === (value as ICompanyProfile).id
            }
            getOptionKey={(option) => (option as ICompanyProfile).id as string}
          />
        }
        action={
          <Button variant="contained" onClick={handleCloseCompanyLinkDialog}>
            {t('contacts.detail.linked_companies.associate')}
          </Button>
        }
      />
    </>
  );
}
